/**
 * TenantSwitcher — platform-admin "view as" dropdown.
 *
 * Visible only when the current user's roles include `platform_administrator`
 * (the backend `resolve_tenant()` gate is the actual security boundary; this
 * component is UX-only).
 *
 * Selecting a tenant sets `altara.assumed_tenant_id` in localStorage; the
 * axios interceptor in @/lib/api attaches `X-Tenant-Id: <that>` to every
 * outbound request. A CustomEvent lets subscribed components re-fetch
 * without a page reload.
 *
 * Two demo wins per BRD:
 *   1. Prove tenant isolation visually — switch tenants and watch the
 *      dashboard data change with no cross-leak.
 *   2. Walk a prospect through what THEIR view will look like without
 *      spinning up a real institution account.
 */
import { useEffect, useState } from "react";
import { Building2, Eye, X, ChevronDown, Check } from "lucide-react";
import api, { getAssumedTenant, setAssumedTenant } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function TenantSwitcher() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [assumed, setAssumed] = useState(getAssumedTenant());
  const [open, setOpen] = useState(false);

  const isPlatformAdmin = user?.roles?.includes("platform_administrator");

  useEffect(() => {
    if (!isPlatformAdmin) return;
    api.get("/admin/tenants")
      .then((r) => setTenants(r.data || []))
      .catch(() => setTenants([]));
  }, [isPlatformAdmin]);

  useEffect(() => {
    const on = (e) => setAssumed(e.detail?.tenantId || "");
    window.addEventListener("altara:assumed-tenant-changed", on);
    return () => window.removeEventListener("altara:assumed-tenant-changed", on);
  }, []);

  if (!isPlatformAdmin) return null;

  const assumedTenant = tenants.find((t) => t.tenant_id === assumed);
  const active = !!assumed && !!assumedTenant;

  const choose = (id) => {
    setAssumedTenant(id);
    setAssumed(id);
    setOpen(false);
    // Nudge react-query + any hand-rolled useEffects to re-fetch. Cheapest
    // way is a soft reload; most admin pages fetch on mount.
    setTimeout(() => window.location.reload(), 80);
  };

  return (
    <div className="relative mx-3 mb-2" data-testid="tenant-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="tenant-switcher-toggle"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-sm border text-[12px] transition"
        style={{
          borderColor: active ? "#C9A24A" : "#E2E8F0",
          background: active ? "#FEF3C71A" : "white",
        }}
      >
        <Building2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: active ? "#C9A24A" : "#64748B" }} />
        <div className="flex-1 text-left min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">
            {active ? "Viewing as" : "Platform admin"}
          </div>
          <div className="font-semibold truncate" style={{ color: "#0A1F44" }}>
            {active ? assumedTenant.name : "Altara (own view)"}
          </div>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>

      {active && (
        <button
          data-testid="tenant-switcher-clear"
          onClick={(e) => { e.stopPropagation(); choose(""); }}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-300 flex items-center justify-center hover:border-rose-400"
          title="Revert to own tenant"
        >
          <X className="w-3 h-3 text-slate-500" />
        </button>
      )}

      {open && (
        <div
          data-testid="tenant-switcher-menu"
          className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-80 overflow-y-auto"
        >
          <div className="px-3 py-2 text-[9px] uppercase tracking-widest text-slate-500 border-b border-slate-100 flex items-center gap-1.5">
            <Eye className="w-3 h-3" /> Switch view
          </div>
          <button
            onClick={() => choose("")}
            data-testid="tenant-switcher-option-own"
            className="w-full flex items-center gap-2 px-3 py-2 text-[12px] hover:bg-slate-50 text-left"
          >
            <div className="w-4 flex items-center justify-center">
              {!active && <Check className="w-3 h-3" style={{ color: "#065F46" }} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold" style={{ color: "#0A1F44" }}>Altara (own view)</div>
              <div className="text-[10px] text-slate-500">Everything, unfiltered</div>
            </div>
          </button>
          <div className="border-t border-slate-100" />
          {tenants.map((t) => (
            <button
              key={t.tenant_id}
              data-testid={`tenant-switcher-option-${t.tenant_id}`}
              onClick={() => choose(t.tenant_id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-[12px] hover:bg-slate-50 text-left"
            >
              <div className="w-4 flex items-center justify-center">
                {assumed === t.tenant_id && <Check className="w-3 h-3" style={{ color: "#065F46" }} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate" style={{ color: "#0A1F44" }}>{t.name}</div>
                <div className="text-[10px] text-slate-500 truncate font-mono">{t.tenant_id}</div>
              </div>
              {t.sector && (
                <span className="text-[9px] uppercase tracking-widest text-slate-400">{t.sector}</span>
              )}
            </button>
          ))}
          {tenants.length === 0 && (
            <div className="px-3 py-4 text-[11px] text-slate-500 text-center">No tenants seeded yet.</div>
          )}
        </div>
      )}
    </div>
  );
}
